<template>
  <div>
    <!-- Filtros -->
    <b-card class="mb-2">
      <b-card-header>
        <h5 class="mb-0">DRE – Demonstração do Resultado do Exercício</h5>
      </b-card-header>
      <b-card-body>
        <b-row>
          <b-col cols="12" md="4">
            <b-form-group label="Data Inicial">
              <b-form-input v-model="filtro.dataInicio" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4">
            <b-form-group label="Data Final">
              <b-form-input v-model="filtro.dataFim" type="date" />
            </b-form-group>
          </b-col>
          <b-col cols="12" md="4" class="d-flex align-items-end">
            <b-button variant="primary" class="w-100" :disabled="loading" @click="buscar">
              <b-spinner v-if="loading" small class="mr-1" />
              <span v-else><feather-icon icon="SearchIcon" class="mr-1" /></span>
              Gerar DRE
            </b-button>
          </b-col>
        </b-row>
      </b-card-body>
    </b-card>

    <!-- Resultado -->
    <b-card v-if="dados">
      <b-card-header class="d-flex justify-content-between align-items-center">
        <h5 class="mb-0">Resultado — {{ formatDate(dados.dataInicio) }} a {{ formatDate(dados.dataFim) }}</h5>
        <div class="d-flex">
          <b-button variant="success" size="sm" @click="exportarCsv">
            <feather-icon icon="DownloadIcon" class="mr-1" />CSV
          </b-button>
          <b-button variant="danger" size="sm" class="ml-1" @click="imprimirPdf" :disabled="loadingPdf">
            <b-spinner v-if="loadingPdf" small class="mr-1" />
            <feather-icon v-else icon="PrinterIcon" class="mr-1" />PDF
          </b-button>
        </div>
      </b-card-header>
      <b-card-body>
        <!-- Receitas -->
        <h6 class="text-success mb-1">RECEITAS</h6>
        <b-table :items="dados.receitas" :fields="camposSimples" striped responsive>
          <template #cell(contaCodigo)="{ item }">{{ item.contaCodigo }}</template>
          <template #cell(contaNome)="{ item }">{{ item.contaNome }}</template>
          <template #cell(total)="{ item }">
            <span class="text-success">{{ formatMoeda(item.total) }}</span>
          </template>
          <template #custom-foot>
            <b-tr class="font-weight-bold">
              <b-td colspan="2">TOTAL RECEITAS</b-td>
              <b-td class="text-success text-right">{{ formatMoeda(dados.totalReceitas) }}</b-td>
            </b-tr>
          </template>
        </b-table>

        <!-- Despesas -->
        <h6 class="text-danger mt-2 mb-1">DESPESAS</h6>
        <b-table :items="dados.despesas" :fields="camposSimples" striped responsive>
          <template #cell(total)="{ item }">
            <span class="text-danger">{{ formatMoeda(item.total) }}</span>
          </template>
          <template #custom-foot>
            <b-tr class="font-weight-bold">
              <b-td colspan="2">TOTAL DESPESAS</b-td>
              <b-td class="text-danger text-right">{{ formatMoeda(dados.totalDespesas) }}</b-td>
            </b-tr>
          </template>
        </b-table>

        <!-- Resultado Final -->
        <b-card :bg-variant="dados.resultado >= 0 ? 'success' : 'danger'" text-variant="white" class="mt-2 text-white">
          <div class="d-flex justify-content-between align-items-center">
            <h5 class="mb-0 text-white">RESULTADO DO EXERCÍCIO</h5>
            <h4 class="mb-0 font-weight-bold text-white">{{ formatMoeda(dados.resultado) }}</h4>
          </div>
        </b-card>
      </b-card-body>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput,
  BButton, BButtonGroup, BSpinner, BTable, BTr, BTd
} from 'bootstrap-vue'
import contabilServices from '@/api/financialModule/contabilServices'
import mixinsGlobal from '@/mixins'

export default {
  name: 'DreReport',
  components: { BCard, BCardHeader, BCardBody, BRow, BCol, BFormGroup, BFormInput, BButton, BButtonGroup, BSpinner, BTable, BTr, BTd },
  mixins: [mixinsGlobal],
  data() {
    const hoje = new Date()
    const dataInicio = `${hoje.getFullYear()}-${String(hoje.getMonth() + 1).padStart(2, '0')}-01`
    const dataFim = hoje.toISOString().substring(0, 10)
    return {
      filtro: { dataInicio, dataFim },
      dados: null,
      loading: false,
      loadingPdf: false,
      camposSimples: [
        { key: 'contaCodigo', label: 'Código' },
        { key: 'contaNome', label: 'Plano de Conta' },
        { key: 'total', label: 'Total', tdClass: 'text-right' }
      ]
    }
  },
  methods: {
    async buscar() {
      this.loading = true
      try {
        const res = await contabilServices.getDre({ dataInicio: this.filtro.dataInicio, dataFim: this.filtro.dataFim })
        this.dados = res.data
      } catch (e) {
        this.MensagemError('Erro', 'Falha ao gerar DRE.')
      } finally {
        this.loading = false
      }
    },
    formatDate(d) {
      if (!d) return ''
      const [y, m, day] = (Array.isArray(d) ? `${d[0]}-${String(d[1]).padStart(2,'0')}-${String(d[2]).padStart(2,'0')}` : d).split('-')
      return `${day}/${m}/${y}`
    },
    formatMoeda(v) {
      if (v == null) return 'R$ 0,00'
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)
    },
    exportarCsv() {
      if (!this.dados) return
      const linhas = [['Tipo', 'Código', 'Plano de Conta', 'Total']]
      this.dados.receitas.forEach(r => linhas.push(['Receita', r.contaCodigo, r.contaNome, r.total]))
      linhas.push(['TOTAL RECEITAS', '', '', this.dados.totalReceitas])
      this.dados.despesas.forEach(d => linhas.push(['Despesa', d.contaCodigo, d.contaNome, d.total]))
      linhas.push(['TOTAL DESPESAS', '', '', this.dados.totalDespesas])
      linhas.push(['RESULTADO', '', '', this.dados.resultado])
      const csv = linhas.map(l => l.join(';')).join('\n')
      const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a'); a.href = url; a.download = 'dre.csv'; a.click()
    },
    imprimirPdf() {
      if (!this.dados) return
      this.loadingPdf = true
      this.$store.dispatch('reportModule/printReportDre', this.dados).then(res => {
        if (res.error) {
          this.MensagemError('Erro', res.error)
        }
      }).finally(() => {
        this.loadingPdf = false
      })
    }
  }
}
</script>
